import { useEffect, useState } from 'react';
import { DEFAULT_CURRENCY, type Currency } from '../config/pricing';

const STORAGE_KEY = 'folioworth_currency';

/**
 * Which currency to quote.
 *
 * Detection comes from Cloudflare's CF-IPCountry header, read server-side and
 * echoed by /api/v1/geo. Cloudflare already fronts every request, so this adds
 * no vendor, no latency, and no IP address is read or stored by us.
 *
 * Geo-IP is wrong often enough — VPNs, travel, corporate proxies — that an
 * explicit choice must win and must stick. A Canadian on a US VPN should not be
 * stuck looking at USD with no way out.
 */
export function useCurrency(): [Currency, (c: Currency) => void] {
  const [currency, setCurrency] = useState<Currency>(() => {
    const saved = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY);
    return saved === 'CAD' || saved === 'USD' ? saved : DEFAULT_CURRENCY;
  });

  useEffect(() => {
    // An explicit choice is never overridden by detection.
    if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) return;

    let cancelled = false;
    fetch('/api/v1/geo')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.currency === 'CAD') setCurrency('CAD');
      })
      // Detection failing is not an error: the default already applies.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const choose = (c: Currency) => {
    setCurrency(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch { /* private mode */ }
  };

  return [currency, choose];
}
